import { AlertTriangle, Download, Info, Pencil, RefreshCw, Trash2 } from "lucide-react";
import { type FC, useState } from "react";

import { m } from "@recobra/i18n/messages";
import type { DocumentType } from "@recobra/schemas/document";
import { DocumentTypeSchema } from "@recobra/schemas/document";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@recobra/ui/alert-dialog";
import { AttachmentAction, AttachmentActions } from "@recobra/ui/attachment";
import { Button } from "@recobra/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverTitle,
  PopoverTrigger,
} from "@recobra/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@recobra/ui/select";
import { Spinner } from "@recobra/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@recobra/ui/tooltip";

import { sortByLabel } from "../lib";
import { getDocumentDetail, getDocumentType, isUploadInProgress } from "./UploadAttachmentState";
import { getDocumentId, type UploadRow } from "./UploadTypes";

export const UploadEntryActions: FC<{
  row: UploadRow;
  showAnalysisDetails?: boolean;
  showVideoAnalysis?: boolean;
  onDownload?: (row: UploadRow) => void;
  onRemove?: (row: UploadRow) => void;
  removeAllowed?: (row: UploadRow) => boolean;
  removeDisabled?: (row: UploadRow) => boolean;
  onRetry?: (row: UploadRow) => void;
  onOverride?: (row: UploadRow, documentType: DocumentType) => void;
  overridePending?: boolean;
}> = ({
  row,
  showAnalysisDetails,
  showVideoAnalysis = false,
  onDownload,
  onRemove,
  removeAllowed,
  removeDisabled,
  onRetry,
  onOverride,
  overridePending,
}) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [selectedOverrideType, setSelectedOverrideType] = useState<DocumentType>();
  const doc = row.doc;
  const detail = getDocumentDetail(doc, showAnalysisDetails, showVideoAnalysis);
  const DetailIcon = detail?.problematic ? AlertTriangle : Info;
  const documentType = getDocumentType(doc);
  const documentTypeOptions = sortByLabel(
    DocumentTypeSchema.options,
    (documentType) => m.document_type_label({ documentType }),
    "other",
  );
  const canRemove = Boolean(
    onRemove &&
    (removeAllowed?.(row) ?? true) &&
    (row.kind === "document" ||
      (row.entry.phase.status !== "uploading" && row.entry.phase.status !== "requesting-url")),
  );
  const canRetry = Boolean(
    onRetry &&
    row.kind === "upload" &&
    row.entry.phase.status === "error" &&
    row.entry.phase.retryable !== false,
  );
  const canOverride = Boolean(onOverride && getDocumentId(row) && doc.status === "classified");

  const closeOverride = () => {
    setOverrideOpen(false);
    setSelectedOverrideType(undefined);
  };

  if (!detail && !canOverride && !canRetry && !canRemove && !onDownload) return null;

  return (
    <AttachmentActions
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {detail && (
        <Tooltip>
          <TooltipTrigger
            render={
              <AttachmentAction
                variant="ghost"
                size="icon-sm"
                title={detail.label}
                aria-label={detail.label}
              />
            }
          >
            <DetailIcon />
          </TooltipTrigger>
          <TooltipContent
            side="top"
            align="end"
            className="max-w-80 flex-col items-start whitespace-pre-wrap"
          >
            <span className="font-medium">{detail.label}</span>
            <span>{detail.text}</span>
          </TooltipContent>
        </Tooltip>
      )}
      {canOverride && (
        <Popover
          open={overrideOpen}
          onOpenChange={(nextOpen) => (nextOpen ? setOverrideOpen(true) : closeOverride())}
        >
          <PopoverTrigger
            render={
              <AttachmentAction
                variant="ghost"
                size="icon-sm"
                title={m.doc_override_type()}
                aria-label={m.doc_override_type()}
              />
            }
          >
            <Pencil />
          </PopoverTrigger>
          <PopoverContent side="left" className="w-64">
            <PopoverTitle>{m.doc_override_type()}</PopoverTitle>
            <PopoverDescription className="sr-only">{m.doc_override_type()}</PopoverDescription>
            <Select
              value={selectedOverrideType}
              onValueChange={(value) => setSelectedOverrideType(DocumentTypeSchema.parse(value))}
            >
              <SelectTrigger className="mt-2 w-full">
                <SelectValue
                  placeholder={
                    documentType
                      ? m.document_type_label({ documentType })
                      : m.doc_status_classified()
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {documentTypeOptions.map((type) => (
                  <SelectItem key={type} value={type}>
                    {m.document_type_label({ documentType: type })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-2 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={closeOverride}>
                {m.case_cancel()}
              </Button>
              <Button
                size="sm"
                disabled={!selectedOverrideType || overridePending}
                onClick={() => {
                  if (!selectedOverrideType) return;
                  onOverride?.(row, selectedOverrideType);
                  closeOverride();
                }}
              >
                {overridePending && <Spinner data-icon="inline-start" />}
                {m.doc_override_confirm()}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      )}
      {canRetry && (
        <AttachmentAction
          variant="ghost"
          size="icon-sm"
          title={m.doc_upload_retry()}
          aria-label={m.doc_upload_retry()}
          onClick={() => onRetry?.(row)}
        >
          <RefreshCw />
        </AttachmentAction>
      )}
      {onDownload && (
        <AttachmentAction
          variant="ghost"
          size="icon-sm"
          title={m.doc_download()}
          aria-label={m.doc_download()}
          onClick={() => onDownload(row)}
        >
          <Download />
        </AttachmentAction>
      )}
      {canRemove && (
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogTrigger
            render={
              <AttachmentAction
                variant="ghost"
                size="icon-sm"
                title={m.doc_list_delete()}
                aria-label={m.doc_list_delete()}
                disabled={removeDisabled?.(row) || isUploadInProgress(doc)}
              />
            }
          >
            <Trash2 />
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{m.doc_delete_confirm_title()}</AlertDialogTitle>
              <AlertDialogDescription>{m.doc_delete_confirm_description()}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{m.case_cancel()}</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                onClick={() => {
                  onRemove?.(row);
                  setDeleteOpen(false);
                }}
              >
                {m.doc_list_delete()}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </AttachmentActions>
  );
};
